import { useAuth } from '../contexts/auth-context';
import { Button, Icon, Card } from './ui';
import { fmtTime, fmtDateShort } from '../utils/format';

export function BaanWelcome({ onNavigate }) {
  return (
    <section className="performance-hero" aria-labelledby="baan-title">
      <div className="relative z-10">
        <p className="performance-eyebrow">DE MEENT / ICE PERFORMANCE</p>
        <h1 id="baan-title">Jouw rondes.<br /><span>Jouw vooruitgang.</span></h1>
        <p className="performance-hero-copy">Beleef de baan live. Ontdek je tijden en haal meer uit iedere rit.</p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Button variant="accent" onClick={() => onNavigate('live')}>
            <Icon name="zap" size={18} /> Bekijk live
          </Button>
          <a className="hero-search-link" href="#session-search">
            Zoek je rit <Icon name="chevronRight" size={18} />
          </a>
        </div>
      </div>
      <svg className="hero-track" viewBox="0 0 440 250" fill="none" aria-hidden="true">
        <path d="M130 50H310a75 75 0 0 1 0 150H130a75 75 0 0 1 0-150Z" stroke="currentColor" strokeWidth="30" opacity=".12" />
        <path d="M130 50H310a75 75 0 0 1 0 150H130a75 75 0 0 1 0-150Z" stroke="currentColor" strokeWidth="2" opacity=".6" />
        <path d="M130 63H310a62 62 0 0 1 0 124H130a62 62 0 0 1 0-124Z" stroke="currentColor" opacity=".3" />
        <path d="M130 37H310a88 88 0 0 1 0 176H130a88 88 0 0 1 0-176Z" stroke="currentColor" opacity=".3" />
        <path d="M230 30V220" stroke="currentColor" strokeDasharray="4 7" opacity=".3" />
        <circle cx="310" cy="50" r="7" fill="#B7EDFA" />
        <circle cx="190" cy="200" r="5" fill="#FF985F" />
        <path d="M245 50H290" stroke="#B7EDFA" strokeWidth="3" strokeLinecap="round" />
      </svg>
    </section>
  );
}

export function BaanPersonalShortcut({ sessions, date, onNavigate }) {
  const { loggedIn, transponders, owns } = useAuth();
  if (!loggedIn) return null;
  const ownSession = sessions.find(session => owns(session.transponder));
  return (
    <section className="mobile-personal-shortcut" aria-label="Snel naar jouw ritten">
      <div>
        <strong>Jouw schaatsen</strong>
        <p>{transponders.length === 0 ? 'Koppel je transponder en ontdek je ritten.' : ownSession ? 'Je hebt op deze dag gereden.' : 'Je ritten en records staan voor je klaar.'}</p>
      </div>
      <Button size="sm" variant="primary" onClick={() => {
        if (!transponders.length) onNavigate('profile');
        else if (ownSession) onNavigate('transponder', { transponder: ownSession.transponder, date });
        else onNavigate('myseason');
      }}>{transponders.length === 0 ? 'Koppelen' : ownSession ? 'Jouw rit' : 'Mijn ritten'}</Button>
    </section>
  );
}

export function BaanSidebar({ sessions, statsMap, date, error, onNavigate }) {
  const { loggedIn, owns, transponders, login } = useAuth();
  const ownSessions = sessions.filter(session => owns(session.transponder));

  return (
    <aside className="baan-sidebar flex min-w-0 flex-col gap-4" aria-label="Jouw schaatsen">
      <Card title={loggedIn ? 'Jouw schaatsen' : 'Meer uit iedere ronde'}>
        {loggedIn ? (
          <>
            {transponders.length === 0 ? (
              <>
                <h2 className="text-[21px] font-bold text-ink">Maak de baan persoonlijk</h2>
                <p className="mt-2 mb-4 text-[15px] text-ink-2">Koppel je transponder. Je ritten en records verschijnen daarna bij elkaar.</p>
                <Button variant="primary" block onClick={() => onNavigate('profile')}>Transponder koppelen</Button>
              </>
            ) : (
              <>
                <p className="mb-3 text-[14px] text-ink-3">{date ? `Jouw rit op ${fmtDateShort(date)}` : 'Jouw gekoppelde transponders'}</p>
                {error ? (
                  <p role="status" className="mb-3 text-[15px] text-ink-2">Je ritgegevens zijn tijdelijk niet beschikbaar.</p>
                ) : ownSessions.length > 0 ? ownSessions.map(session => (
                  <button key={session.transponder} className="own-session-link" onClick={() => onNavigate('transponder', { transponder: session.transponder, date })}>
                    <span className="min-w-0">
                      <strong className="block truncate">{session.nickname || session.transponder}</strong>
                      <span className="text-[14px] text-ink-3">Beste ronde</span>
                    </span>
                    <span className="font-mono text-[18px] font-bold">{fmtTime(statsMap[session.transponder]?.bestLapMs)}</span>
                    <Icon name="chevronRight" size={18} />
                  </button>
                )) : (
                  <p className="mb-3 text-[15px] text-ink-2">Geen rit gevonden op deze dag. Je andere ritten staan in je persoonlijke overzicht.</p>
                )}
                <Button variant="primary" block onClick={() => onNavigate('myseason')}>Mijn ritten &amp; records</Button>
              </>
            )}
          </>
        ) : (
          <>
            <h2 className="text-[22px] font-bold leading-tight text-ink">Niet alleen meten.<br />Vooruitgaan.</h2>
            <ol className="onboarding-steps mt-4 mb-5">
              <li><span>01</span><div><strong>Maak je account</strong><p>Jouw plek op de baan.</p></div></li>
              <li><span>02</span><div><strong>Koppel je transponder</strong><p>Vind automatisch je ritten.</p></div></li>
              <li><span>03</span><div><strong>Ontdek je vooruitgang</strong><p>Records, rondes en wedstrijdanalyse.</p></div></li>
            </ol>
            <Button variant="primary" block onClick={login}>Inloggen / account maken</Button>
          </>
        )}
      </Card>
      <Card title="Beleef je prestaties">
        <div className="feature-note"><Icon name="route" size={22} /><div><h3>Bekijk je wedstrijd terug</h3><p>Analyseer snelheid per sectie en speel je wedstrijdrit opnieuw af.</p></div></div>
        <div className="feature-note mt-4"><Icon name="trophy" size={22} /><div><h3>Iedere stap telt</h3><p>Ontdek beste tijden, deelname en persoonlijke vooruitgang in de ranglijst.</p></div></div>
        <Button className="mt-4" variant="outline" block onClick={() => onNavigate('ranking')}>Ontdek de ranglijst</Button>
      </Card>
      {loggedIn && (
        <Button variant="outline" block onClick={() => onNavigate('coach')}>
          <Icon name="compass" size={18} /> Naar coachwerkplek
        </Button>
      )}
    </aside>
  );
}
